import _ from 'lodash'
import {organizationPermissionCodes, permissionCodes} from 'modules/permissions/contants'
import PermissionContext from 'modules/permissions/PermissionContext'
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {connect} from 'react-redux'
import {NavLink, Redirect, useLocation, useRouteMatch} from 'react-router-dom'
import {branch, compose, nest} from 'recompose'
import {getLogin} from 'redux/selectors'
import {staticPaths} from 'routes/staticPaths'
import {createCheckPermissions, hasPermissions} from 'views/AppLocals/factory/createCheckPermissions'
import {NullAsDefault, renderIf} from 'views/Shared'
import WorkspaceActivities from './Activities/WorkspaceActivities'
import WorkspaceFeatures from './components/WorkspaceFeatures'
import WorkspaceExamination from './Examination/WorkspaceExamination'
import WorkspaceHome from './HomePage/WorkspaceHome'
import WorkspaceSetting from './Settings/WorkspaceSetting'
import WorkspaceContext from './WorkspaceContext'
import {WorkspaceProvider} from './WorkspaceProvider'
import WorkspaceLandingPage from './WorkspaceLandingPage'
import WorkspaceMembers from './Host/WorkspaceMembers'
import WorkspaceCourse from './Course/WorkspaceCourse'
import WorkspaceCatholicHousehold from './CatholicHousehold/WorkspaceCatholicHousehold'
import LoadingBlock from '../Loading/LoadingBlock'
import WorkspaceShop from './Shop/WorkspaceShop'
import WorkspaceFinance from './Finance/WorkspaceFinance'
import {validateServiceType} from '../Organization/functions/validateServiceType'
import ContentLayout from '../../components/layouts/Default/ContentLayout'
import FlyWrapper from '../AppLocals/Templates/FlyWrapper'
import useTranslate from '../../modules/local/useTranslate'
import {SearchGroupIcon} from '../../components/AppIcon'
import classNames from 'classnames'
import {Avatar} from 'antd'
import {BiSolidChurch} from 'react-icons/bi'
import {BsChevronDown, BsGrid} from 'react-icons/bs'
import {useRouteSearchParam} from '../../envs/_current/history'
import DrawDialog from '../../components/drawer/dialog/DrawDialog'
import AdminOrganizations from './components/AdminOrganizations'
import getTitle from '../../helpers/getTitle'
import {useAppConfig} from '../../modules/local/AppConfigContext'
import {getId} from '../../apis/model/base'
import UnderDevelopment from '../Shared/components/UnderDevelopment'
import {isDevEnviroment} from '../../envs/ForDevelop'
import {PendingEnrollmentsBadge, PendingHouseholdsBadge} from './components/WorkspaceBadges'

// ─── Route keys ──────────────────────────────────────────────────────────────

export const WorkSpaceActions = Object.freeze({
    HOME: 'dashboard',
    COMMUNICATIONS: 'activities',
    MEMBER: 'member',
    GAMES: 'games',
    COURSE: 'course',
    PRODUCT: 'product',
    FINANCE: 'finance',
    HOUSEHOLD: 'households',
    SETTING: 'setting',
})

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isServiceEnabled = (org, serviceKey) =>
    validateServiceType(_.get(org, 'service_type', '0000000000000000'), serviceKey)

// ─── Route definitions ───────────────────────────────────────────────────────

const routeDefinitions = [
    {
        key: WorkSpaceActions.HOME,
        label: 'overview',
        component: WorkspaceHome,
    },
    {
        key: WorkSpaceActions.COMMUNICATIONS,
        label: 'communications',
        component: WorkspaceActivities,
    },
    {
        key: WorkSpaceActions.MEMBER,
        label: 'members & organization',
        component: WorkspaceMembers,
        required_permissions: [organizationPermissionCodes.organizationManageMember],
    },
    {
        key: WorkSpaceActions.COURSE,
        label: 'training & education',
        component: WorkspaceCourse,
    },
    {
        key: WorkSpaceActions.HOUSEHOLD,
        label: 'records & sacraments',
        component: WorkspaceCatholicHousehold,
        isOptional: true,
    },
    {
        key: WorkSpaceActions.FINANCE,
        label: 'finance & charity support',
        component: WorkspaceFinance,
        isOptional: true,
    },
    {
        key: WorkSpaceActions.GAMES,
        label: 'games',
        component: WorkspaceExamination,
        isOptional: true,
        isServiceDisabled: (org) => !isServiceEnabled(org, 'examination'),
    },
    {
        key: WorkSpaceActions.SETTING,
        label: 'system settings',
        component: WorkspaceSetting,
    },
    {
        key: WorkSpaceActions.PRODUCT,
        label: 'shops',
        component: WorkspaceShop,
        isOptional: true,
        isServiceDisabled: (org) => !isServiceEnabled(org, 'product'),
    },
]

export const routes = routeDefinitions.map(
    ({key, required_permissions = [], component, isUnderDevelopment, ...rest}) => ({
        key,
        ...rest,
        isUnderDevelopment,
        required_permissions,
        component: isUnderDevelopment
            ? () => <UnderDevelopment/>
            : required_permissions.length
                ? createCheckPermissions(required_permissions)(component)
                : component,
    })
)

// ─── Sub-menu: Home ──────────────────────────────────────────────────────────

export const HOME_SUB_ITEMS = [
    {key: 'summary-board', label: 'summary board'},
    {key: 'notifications', label: 'notifications'},
    // {key: 'activity-calendar', label: 'activity calendar'},
    // {key: 'recent-activities', label: 'recent activities', isUnderDevelopment: true},
]

// ─── Sub-menu: Communications ────────────────────────────────────────────────

export const COMMUNICATIONS_SUB_ITEMS = [
    {key: 'announcements', label: 'announcements'},
    // { key: 'articles',         label: 'articles',        isUnderDevelopment: true },
    {key: 'events', label: 'events', required_permissions: [organizationPermissionCodes.organizationManageEvent]},
    {
        key: 'channels',
        label: 'content channels',
        required_permissions: [organizationPermissionCodes.organizationManageChannel]
    },
    {key: 'activity-schedule', label: 'activity schedule'},
    // { key: 'media-library',    label: 'media library',   isUnderDevelopment: true },
    {
        key: 'moderation',
        label: 'moderation',
        required_permissions: [organizationPermissionCodes.organizationManageChannel]
    },
]

// ─── Sub-menu: Members & Organization ────────────────────────────────────────

export const MEMBER_SUB_ITEMS = [
    {key: 'dashboard', label: 'overview'},
    {key: 'org-members', label: 'member list'},
    {key: 'personal-profile', label: 'personal profile'},
    {
        key: 'groups-units', label: 'groups & units',
        children: [
            {key: 'org-groups', label: 'group list'},
            {key: 'org-tree', label: 'organization tree'},
        ],
    },
    {
        key: 'org-relations', label: 'organization relations',
        children: [
            {key: 'governing_organizations', label: 'governing organizations'},
            {key: 'subordinate_units', label: 'subordinate units'},
            {key: 'peer-members', label: 'affiliated members'},
        ],
    },
    // {
    //     key: 'stats-classification', label: 'statistics & classification',
    //     children: [
    //         { key: 'member-anniversaries', label: 'member anniversaries', isUnderDevelopment: true },
    //         { key: 'age-group',            label: 'age group',            isUnderDevelopment: true },
    //         { key: 'family-situation',     label: 'family situation',     isUnderDevelopment: true },
    //     ],
    // },
    // { key: 'related-events', label: 'related events' },
]

// ─── Sub-menu: Games (Examination) ───────────────────────────────────────────

export const GAMES_SUB_ITEMS = [
    {
        key: 'fun-quiz', label: 'fun quiz',
        children: [
            {key: 'examination', label: 'examinations'},
            {key: 'examination-records', label: 'exam submissions'},
            {key: 'examination-questions', label: 'question bank'},
            {
                key: 'report', label: 'report',
                children: [
                    {key: 'exam-ranking-report', label: 'ranking report'},
                    {key: 'exam-report-group-by-infos', label: 'report by group'},
                ],
            },
        ],
    },
]

// ─── Sub-menu: Training & Education ─────────────────────────────────────────

export const COURSE_SUB_ITEMS = [
    {
        key: 'overview-group', label: 'training management',
        children: [
            {key: 'dashboard', label: 'dashboard'},
            // { key: 'training-report', label: 'training report',  isUnderDevelopment: true },
            {key: 'curriculum', label: 'curriculum'},
            {key: 'subject', label: 'subjects'},
            {key: 'course-medals', label: 'certificates'},
        ],
    },
    {
        key: 'courses-group', label: 'courses',
        children: [
            {key: 'course', label: 'all courses'},
            {key: 'enrolling-courses', label: 'enrolling courses'},
            {key: 'active-courses', label: 'active courses'},
            {key: 'completed-courses', label: 'completed courses'},
        ],
    },
    {
        key: 'classes-group', label: 'classes',
        children: [
            {key: 'course-class', label: 'class list'},
            {key: 'course-class-timetable', label: 'class timetable'},
        ],
    },
    {
        key: 'training-staff-group', label: 'training personnel',
        children: [
            {key: 'student', label: 'students'},
            {key: 'register-for-course', label: 'enrollments', badge: <PendingEnrollmentsBadge />},
            {key: 'teacher', label: 'instructors'},
            {key: 'parents', label: 'parents'},
            {key: 'identity-resolution', label: 'identity resolution'},
            {key: 'duplicate-students', label: 'duplicate students'},
        ],
    },
    // {
    //     key: 'quiz-group', label: 'quiz & assessment',
    //     children: [
    //         { key: 'question-bank', label: 'question bank', isUnderDevelopment: true },
    //         { key: 'quiz-exam',     label: 'quiz',          isUnderDevelopment: true },
    //         { key: 'ranking',       label: 'ranking',       isUnderDevelopment: true },
    //         { key: 'quiz-results',  label: 'quiz results',  isUnderDevelopment: true },
    //     ],
    // },
    {
        key: 'report-group', label: 'reports',
        children: [
            // { key: 'student-transfer-report', label: 'transfer records' },
            {
                key: 'attendance-report', label: 'attendance reports',
                children: [
                    {key: 'course-class-attendance-report', label: 'attendance by class'},
                    {key: 'course-class-attendance-stats-report', label: 'attendance ratio report'},
                    {key: 'course-class-student-attendance-sheet-report', label: 'attendance sheet report'},
                ],
            },
            {
                key: 'score-report', label: 'grade reports',
                children: [
                    {key: 'course-class-student-results-report', label: 'grade by class'},
                    {key: 'student-results-report', label: 'student detail report'},
                    {key: 'course-student-results-report', label: 'student results by class'},
                ],
            },
        ],
    },
]

// ─── Sub-menu: Shop ──────────────────────────────────────────────────────────

export const SHOP_SUB_ITEMS = []

// ─── Sub-menu: Finance ───────────────────────────────────────────────────────

export const FINANCE_SUB_ITEMS = [
    {key: 'dashboard', label: 'financial overview', isUnderDevelopment: true},
    {key: 'income', label: 'income & contributions', isUnderDevelopment: true},
    {key: 'outcome', label: 'expenses', isUnderDevelopment: true},
    {key: 'fund-budget', label: 'fund & budget', isUnderDevelopment: true},
    {key: 'charity-support', label: 'charity & support', isUnderDevelopment: true},
    {key: 'finance-report', label: 'financial report', isUnderDevelopment: true},
]

// ─── Sub-menu: Records & Sacraments ──────────────────────────────────────────

export const HOUSEHOLD_SUB_ITEMS = [
    {key: 'catholic-household-stats', label: 'household statistics'},
    {key: 'pending-catholic-households', label: 'pending households', badge: <PendingHouseholdsBadge />},
    {key: 'records-and-books', label: 'family records'},
    {
        key: 'catholic-household-query-group', label: 'lookup the sacraments',
        children: [
            {key: 'catholic-household-query-baptism', label: 'sacrament of baptism'},
            {key: 'catholic-household-query-first_communion', label: 'first communion'},
            {key: 'catholic-household-query-confirmation', label: 'sacrament of confirmation'},
            {key: 'catholic-household-query-matrimony', label: 'sacrament of matrimony'},
            {key: 'catholic-household-query-holy_orders', label: 'sacrament of holy orders'},
        ],
    },
]

// ─── Sub-menu: Settings ──────────────────────────────────────────────────────
// key phải khớp với path trong routeEntities (dùng cho ?rel=key)

export const SETTING_SUB_ITEMS = [
    {key: 'overview', label: 'general settings'},
    {
        key: 'profiles',
        label: 'organization information',
        children: [
            {key: 'about-us', label: 'about us'},
            {key: 'photos', label: 'photos'},
            {key: 'profile', label: 'more information'},
            {key: 'categories', label: 'categories'},
            {key: 'organization_verification', label: 'organization verification'},
            {
                key: 'location-contact',
                label: 'location & contact',
                children: [
                    {key: 'location', label: 'location'},
                    {key: 'contact', label: 'contact'},
                ],
            },
        ],
    },
    {
        key: 'permissions-group',
        label: 'permissions & roles',
        children: [
            {key: 'entity-role', label: 'roles & positions'},
            {key: 'permission-role', label: 'access permissions'},
        ],
    },
    {
        key: 'reference-data',
        label: 'reference data',
        children: [
            {key: 'organization-categories', label: 'organization categories'},
            {
                key: 'medal-group',
                label: 'certifications',
                children: [
                    {key: 'medal-list', label: 'certificate list'},
                    {key: 'medal-rewards-history', label: 'award history'},
                ],
            },
        ],
    },
    {key: 'terminology', label: 'terminology & labels'},
    {key: 'delete', label: 'delete organization'},
]

// ─── Sub-items map (route key → sub-items) ───────────────────────────────────

const SUB_ITEMS_MAP = {
    [WorkSpaceActions.HOME]: HOME_SUB_ITEMS,
    [WorkSpaceActions.COMMUNICATIONS]: COMMUNICATIONS_SUB_ITEMS,
    [WorkSpaceActions.MEMBER]: MEMBER_SUB_ITEMS,
    [WorkSpaceActions.GAMES]: GAMES_SUB_ITEMS,
    [WorkSpaceActions.COURSE]: COURSE_SUB_ITEMS,
    [WorkSpaceActions.PRODUCT]: SHOP_SUB_ITEMS,
    [WorkSpaceActions.FINANCE]: FINANCE_SUB_ITEMS,
    [WorkSpaceActions.HOUSEHOLD]: HOUSEHOLD_SUB_ITEMS,
    [WorkSpaceActions.SETTING]: SETTING_SUB_ITEMS,
}

// ─── SectionTemplate ─────────────────────────────────────────────────────────

export const SectionTemplate = ({title, description, renderActionButton}) => (
    <div className="p-1 sm:p-2 w-full">
        <h1 className="text-xl font-bold text-primary uppercase mb-4">{title}</h1>
        <div className="p-4 background border border-color-50 shadow-lg rounded-xl">
            <p className="mb-2 text-base text-color-200 tracking-wider">
                {description}
            </p>
            {renderActionButton && (
                <div className="text-right">
                    {renderActionButton()}
                </div>
            )}
        </div>
    </div>
)

// ─── Dev filter ───────────────────────────────────────────────────────────────
// In non-DEV: removes items with isUnderDevelopment and collapses empty groups

const filterDevItems = (items = []) => {
    if (isDevEnviroment()) return items
    return items
        .filter(item => !item.isUnderDevelopment)
        .map(item => {
            if (!item.children) return item
            const children = filterDevItems(item.children)
            return children.length > 0 ? {...item, children} : null
        })
        .filter(Boolean)
}

// ─── ComingSoonBadge ──────────────────────────────────────────────────────────

const ComingSoonBadge = () => (
    <span className="text-2xs text-red-400 italic font-medium leading-none">
        soon
    </span>
)

// ─── WorkspaceSidebarSubItem ─────────────────────────────────────────────────
// baseUrl = URL gốc của route cha (VD: /workspace/operating)
// KHÔNG bao giờ append ?rel vào baseUrl đã có ?rel

const hasActiveDescendant = (item, rel) => {
    if (!item.children) return false
    return item.children.some(child => child.key === rel || hasActiveDescendant(child, rel))
}

const WorkspaceSidebarSubItem = ({item, baseUrl, permissions}) => {
    const t = useTranslate()
    const isDisabled = !_.isEmpty(item.required_permissions)
        && !hasPermissions(permissions, item.required_permissions)

    const hasChildren = !_.isEmpty(item.children)
    const rel = useRouteSearchParam('rel') || 'home'
    const itemKey = item?.key || 'home'
    const isActive = rel === itemKey
    const isChildActive = hasChildren && hasActiveDescendant(item, rel)
    const [open, setOpen] = useState(isChildActive)

    useEffect(() => {
        if (isChildActive) setOpen(true)
    }, [isChildActive])
    const leafUrl = hasChildren ? null : `${baseUrl}?rel=${itemKey}`

    const linkClass = classNames(
        'flex items-center gap-2 no-underline hover:pl-1 transition-all duration-300',
        isActive ? 'font-semibold text-secondary-600' : 'text-slate-500 hover:text-secondary',
        isDisabled && 'opacity-40 pointer-events-none',
    )

    const toggleOpen = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setOpen(v => !v)
    }

    return (
        <div className="flex flex-col flex-1 gap-1 SidebarSubsItem">
            {hasChildren ? (
                <div
                    onClick={toggleOpen}
                    className={classNames(
                        'p-1 flex items-center gap-2 cursor-pointer',
                        isActive
                            ? 'font-bold text-secondary-700'
                            : open
                            ? 'font-semibold text-slate-600'
                            : 'text-slate-500 hover:text-secondary-600 hover:bg-secondary-50'
                    )}>
                    <span className="flex-1 tracking-wide truncate">
                        {t(item.label)}
                    </span>
                    <BsChevronDown
                        size={14}
                        style={{transform: open ? 'rotate(-90deg)' : ''}}
                        className="SidebarSubItemIcon mr-1 transition-all duration-300"
                    />
                </div>
            ) : (
                <div className="p-1 flex items-center gap-2 transition-all duration-300 SidebarSubsItem">
                    <NavLink to={leafUrl} className={classNames(linkClass, 'flex-1')}>
                        {t(item.label)}
                    </NavLink>
                    {item.badge}
                    {item.isUnderDevelopment && <ComingSoonBadge/>}
                </div>
            )}

            {hasChildren && open && (
                <div className="ml-3 pl-3 border-l border-color-100 flex flex-col gap-1">
                    {item.children.map(child => (
                        <WorkspaceSidebarSubItem
                            key={child.key}
                            item={child}
                            baseUrl={baseUrl}
                            permissions={permissions}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// ─── WorkspaceSidebarItem ────────────────────────────────────────────────────

const WorkspaceSidebarItem = ({route, url, isActive, isDisabled, subItems = [], permissions}) => {
    const t = useTranslate()
    const hasChildren = !_.isEmpty(subItems)
    const [open, setOpen] = useState(isActive && hasChildren)

    useEffect(() => {
        if (isActive && hasChildren) setOpen(true)
    }, [isActive, hasChildren])

    const itemClass = classNames(
        'flex items-center gap-2 transition-all duration-300 no-underline relative',
        isActive ? 'font-semibold bg-secondary-50 text-secondary-600' : 'text-slate-600 hover:background-200',
        isDisabled && 'opacity-40 pointer-events-none'
    )

    const handleClick = (e) => {
        if (hasChildren) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(v => !v)
        }
    }

    return (
        <div className="flex flex-col gap-1 SidebarItem">
            {(isDisabled || !url) ? (
                <div style={{ padding: '0.35rem 0.55rem' }} className={itemClass}>
                    <SidebarItemInner
                        route={route}
                        isActive={isActive}
                        hasChildren={hasChildren}
                        open={open}
                        onClick={handleClick}
                        t={t}
                    />
                </div>
            ) : (
                <NavLink to={url} style={{ padding: '0.35rem 0.55rem' }} className={itemClass}>
                    <SidebarItemInner
                        route={route}
                        isActive={isActive}
                        hasChildren={hasChildren}
                        open={open}
                        onClick={handleClick}
                        t={t}
                    />
                </NavLink>
            )}

            {hasChildren && open && (
                <div className="ml-5 my-1 pl-3 flex flex-col gap-1 border-l border-color-100">
                    {subItems.map(item => (
                        <WorkspaceSidebarSubItem
                            key={item.key}
                            item={item}
                            baseUrl={url}
                            isActive={false}
                            permissions={permissions}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

const SidebarItemInner = ({route, isActive, hasChildren, open, onClick, t}) => (
    <div onClick={onClick} className="flex items-center flex-1 gap-2">
        {isActive && (
            <div className="absolute left-0 h-5 border-l-3 border-secondary-500 rounded-tr-lg rounded-br-lg"/>
        )}
        <div
            style={{width: 25, height: 25}}
            className={classNames(
                'flex flex-center rounded-lg transition-all duration-300',
                isActive && 'bg-secondary-500 text-white'
            )}>
            <SearchGroupIcon
                type={route.key}
                size={isActive ? 15 : 17}
                className="flex-shrink-0"
                FallbackComponent={BsGrid}
            />
        </div>
        <span className="flex-1 tracking-wide leading-snug truncate">
            {t(route.label)}
        </span>
        {route.isUnderDevelopment && <ComingSoonBadge/>}
        {hasChildren && (
            <BsChevronDown
                size={14}
                style={{transform: open ? 'rotate(-90deg)' : ''}}
                className="SidebarItemIcon transition-all duration-300"
            />
        )}
    </div>
)

// ─── WorkspaceOrgSwitcher ────────────────────────────────────────────────────

const WorkspaceOrgSwitcher = ({organization, type}) => {
    const t = useTranslate()
    return (
        <DrawDialog
            width={540}
            closable={true}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('select organization')}
                </div>
            }
            component={
                <div
                    className="p-2 rounded-xl flex items-center gap-2 border border-color-50 hover:border-secondary-500 hover:shadow-md cursor-pointer"
                    style={{background: 'var(--background)'}}>
                    <Avatar
                        size={40}
                        icon={<BiSolidChurch size={20} className="text-primary-400"/>}
                        className="flex flex-center bg-primary-50 rounded-lg"
                    />
                    <div className="flex flex-col flex-1 gap-1">
                        <span className="font-bold tracking-wide leading-tight max-lines-1">
                            {getTitle(organization)}
                        </span>
                        {renderIf(
                            type,
                            <div className="font-medium text-2xs text-blue-500 tracking-wider leading-tight">
                                {t(type)}
                            </div>
                        )}
                    </div>
                    <BsChevronDown size={12} className="transition-all duration-300"/>
                </div>
            }>
            {(isToggle, toggle) => isToggle && (
                <AdminOrganizations orgId={getId(organization)} onClose={toggle}/>
            )}
        </DrawDialog>
    )
}

// ─── Workspace ───────────────────────────────────────────────────────────────

const Workspace = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const isReady = _.has(organization, 'available_actions')
    const permissions = useContext(PermissionContext)
    const {path: root} = useRouteMatch()
    const location = useLocation()
    const {org_type} = organization ?? {}
    const {org_types = {}} = useAppConfig('org_types')
    const type = _.get(org_types, org_type)

    const getPath = useCallback(
        (key) => [root.replace(/\/$/, ''), key].join('/'),
        [root]
    )

    const currentKey = useMemo(
        () => location?.pathname?.split('/').filter(Boolean).pop(),
        [location?.pathname]
    )

    const menu = useMemo(() => (
        <FlyWrapper top={0}>
            <div className="pr-0.5 py-3 flex flex-col gap-1">
                {/* Logo */}
                {/*<div className="flex items-center gap-2">*/}
                {/*    <AppLogoSvg size={36}/>*/}
                {/*    <div className="flex flex-col">*/}
                {/*        <AppTextLogoFillSvg size={22}/>*/}
                {/*        <span className="text-2xs text-color-300 italic tracking-wider">*/}
                {/*            {`${APP_COPYRIGHT}.`}*/}
                {/*        </span>*/}
                {/*    </div>*/}
                {/*</div>*/}

                <div className="px-2 flex flex-col gap-1">
                    {/* Org switcher */}
                    <label className="font-medium text-xs text-gray-500 tracking-widest uppercase">
                        {t('choose organization')}
                    </label>
                    <WorkspaceOrgSwitcher organization={organization} type={type}/>
                </div>

                <div className="my-2 border-b border-color-100"/>

                {/* Nav items */}
                <div className="flex flex-col workspace-menu-sidebar">
                    {routes.map((route) => {
                        const isDisabled = !hasPermissions(permissions, route.required_permissions)
                        const isServiceOff = route.isServiceDisabled?.(organization) ?? false
                        const url = getPath(route.key)
                        const isActive = currentKey === route.key

                        return (
                            <WorkspaceSidebarItem
                                key={route.key}
                                route={route}
                                url={url}
                                isActive={isActive}
                                isDisabled={isDisabled || isServiceOff}
                                subItems={filterDevItems(SUB_ITEMS_MAP[route.key] ?? [])}
                                permissions={permissions}
                            />
                        )
                    })}
                </div>

                <div className="h-16"/>
            </div>
        </FlyWrapper>
    ), [permissions, organization, currentKey, getPath, type])

    return (
        <div className="flex flex-1">
            <ContentLayout
                title={null}
                renderHeader={NullAsDefault}
                menu={menu}
                renderRight={NullAsDefault}>
                {isReady ? (
                    <WorkspaceFeatures
                        activeKey={t(_.find(routes, {key: currentKey})?.label)}
                        routes={routes}
                        isDisabled={({required_permissions}) =>
                            !hasPermissions(permissions, required_permissions)
                        }
                        defaultAction={WorkSpaceActions.HOME}
                    />
                ) : (
                    <LoadingBlock/>
                )}
            </ContentLayout>
        </div>
    )
}

// ─── Export ──────────────────────────────────────────────────────────────────

export default compose(
    connect((state) => {
        const login = getLogin(state)
        const primary_org = _.get(login, 'primary_org')
        return {
            primary_org,
            [permissionCodes.canEdit]: Boolean(!_.isEmpty(primary_org)),
        }
    }),
    branch(
        ({primary_org}) => _.isEmpty(primary_org),
        () => () => <WorkspaceLandingPage/>
    ),
    branch(
        (props) => !_.get(props, permissionCodes.canEdit),
        () => () => <Redirect to={staticPaths.start}/>
    )
)(nest(WorkspaceProvider, Workspace))